import React from 'react';
import {
  Divider,
  Accordion,
  Button,
  Form,
  Grid,
  Header,
  Icon,
} from 'semantic-ui-react';
import { Field } from 'redux-form';
import PropTypes from 'prop-types';

import { TransactionHistoryReportEnum } from 'Common/FieldEnum';
import {
  SelectField,
  DatePickerField,
  DropdownField,
} from 'Common/SemanticWrapper';
import { DATE_FORMAT_FOR_UI } from 'Common/Constants';
import { DatePickerCustomButton, TitledCard } from 'Components';
import { TRANSACTION_HISTORY_REPORT_FORM_NAME } from 'Common/FormEnum';
import {
  makeOptionsData,
  makeItemCodeFirstOptionsData,
  makeItemDescriptionFirstOptionsData,
} from 'Common/FormHelper';
import { GenericFiltersContainer } from 'Components/filters/container';
import { ReduxFormButtonGroup } from 'Components/forms/ReduxFormButtonGroup';
import SelectSitesTable from 'Common/SelectSitesTable';
import { printOptions, itemSortOption, activeStatusOptions } from '../helper';

const TransactionDetailsReportComponent = props => {
  const {
    handleCollapsePanelClick,
    activeIndexes,
    clearFilters,
    handleSubmit,
    setSelectedSites,
    itemSortOptionsValue,
    dropdownItems,
    handleChange,
    filterValues,
    bidGroupData,
    itemGroupData,
    acctCategoryData,
    vendorData,
    printOptionsValue,
    transactionTypesData,
    initialSelectedSiteIds,
    loadingSitesData,
    reactTableRef,
  } = props;

  const fields = [
    {
      name: TransactionHistoryReportEnum.ItemStatus,
      component: SelectField,
      type: 'text',
      options: activeStatusOptions,
      label: 'ITEM STATUS',
      maxLength: 20,
    },
    {
      name: TransactionHistoryReportEnum.BidGroupId,
      component: SelectField,
      type: 'text',
      options: makeOptionsData(bidGroupData),
      label: 'BID GROUP',
      maxLength: 20,
    },
    {
      name: TransactionHistoryReportEnum.ItemGroupId,
      component: SelectField,
      type: 'text',
      options: makeOptionsData(itemGroupData),
      label: 'ITEM GROUP',
    },
    {
      name: TransactionHistoryReportEnum.AccountCategoryId,
      component: SelectField,
      type: 'text',
      options: makeOptionsData(acctCategoryData),
      label: 'ACCT. CATEGORY',
    },
    {
      name: TransactionHistoryReportEnum.VendorId,
      component: SelectField,
      type: 'text',
      options: makeOptionsData(vendorData),
      label: 'DISTRIBUTOR',
    },
  ];
  const itemsDropDownOptions = itemSortOptionsValue
    ? makeItemCodeFirstOptionsData(dropdownItems)
    : makeItemDescriptionFirstOptionsData(dropdownItems);

  return (
    <Form onSubmit={handleSubmit}>
      <Accordion fluid styled>
        <Accordion.Title
          active={activeIndexes.indexOf(0) !== -1}
          index={0}
          onClick={handleCollapsePanelClick}
        >
          <Icon name="dropdown" />
          <span className="text-orange">Filter By</span>
        </Accordion.Title>

        <Accordion.Content active={activeIndexes.indexOf(0) !== -1}>
          <Grid className="grid-spacing">
            <Grid.Row columns={2} className={'sortItemCodeDescription'}>
              <Grid.Column verticalAlign={'bottom'} width={8}>
                <ReduxFormButtonGroup
                  options={itemSortOption}
                  value={itemSortOptionsValue}
                  formName={TRANSACTION_HISTORY_REPORT_FORM_NAME}
                  fieldName={TransactionHistoryReportEnum.ItemSortOptions}
                  type={'button'}
                />
              </Grid.Column>
              <Grid.Column
                width={6}
                style={{
                  marginBottom: 19,
                }}
                verticalAlign={'top'}
              >
                <Field
                  component={SelectField}
                  options={itemsDropDownOptions}
                  label={'Item'}
                  name={TransactionHistoryReportEnum.ItemId}
                  type={'text'}
                />
              </Grid.Column>
            </Grid.Row>
            <Grid.Row columns={2}>
              <Grid.Column>
                <div style={{ paddingTop: 25 }}>
                  <Header as="h4">Transaction Date Range</Header>
                </div>
              </Grid.Column>
            </Grid.Row>

            <Grid.Row
              columns={4}
              style={{
                marginTop: -14,
              }}
            >
              <Grid.Column>
                <Field
                  component={DatePickerField}
                  dateFormat={DATE_FORMAT_FOR_UI}
                  label={'From:'}
                  name={TransactionHistoryReportEnum.DateFrom}
                  type={'text'}
                  customInput={<DatePickerCustomButton />}
                />
              </Grid.Column>
              <Grid.Column>
                <Field
                  component={DatePickerField}
                  dateFormat={DATE_FORMAT_FOR_UI}
                  label={'To:'}
                  name={TransactionHistoryReportEnum.DateTo}
                  type={'text'}
                  customInput={<DatePickerCustomButton />}
                />
              </Grid.Column>
            </Grid.Row>

            <GenericFiltersContainer
              fields={fields}
              onChange={handleChange}
              filterValues={filterValues}
            />

            <Grid.Row columns={2}>
              <Grid.Column width={12}>
                <Field
                  options={makeOptionsData(transactionTypesData)}
                  placeholder="Select types"
                  search
                  component={DropdownField}
                  name={TransactionHistoryReportEnum.TypeId}
                  selection
                  fluid
                  multiple
                  label={'Transaction Type'}
                />
              </Grid.Column>
              <Grid.Column
                floated={'right'}
                textAlign={'right'}
                style={{
                  marginTop: 32,
                }}
                width={4}
              >
                <Button
                  content="Clear Filters"
                  onClick={clearFilters}
                  type={'button'}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Accordion.Content>

        <Accordion.Title
          active={activeIndexes.indexOf(1) !== -1}
          index={1}
          onClick={handleCollapsePanelClick}
        >
          <Icon name="dropdown" />
          <span className="text-orange">Sort By</span>
        </Accordion.Title>

        <Accordion.Content active={activeIndexes.indexOf(1) !== -1}>
          <Grid className="tight">
            <Grid.Row columns={3}>
              <Grid.Column width={12}>
                <ReduxFormButtonGroup
                  options={printOptions}
                  value={printOptionsValue}
                  formName={TRANSACTION_HISTORY_REPORT_FORM_NAME}
                  fieldName={TransactionHistoryReportEnum.SortBy}
                  type={'button'}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Accordion.Content>
      </Accordion>
      <Divider horizontal />
      <TitledCard>
        {!loadingSitesData && initialSelectedSiteIds.length && (
          <SelectSitesTable
            onChange={setSelectedSites}
            initialSelectedSiteIds={initialSelectedSiteIds}
            isActive={true}
            isLicensed={false}
            showPageSizeOptions={true}
            defaultPageSize={20}
            filterable={true}
            sortable={true}
            tableProps={{ ref: reactTableRef }}
          />
        )}
      </TitledCard>
    </Form>
  );
};

TransactionDetailsReportComponent.propTypes = {
  dropdownItems: PropTypes.array.isRequired,
  bidGroupData: PropTypes.array.isRequired,
  itemGroupData: PropTypes.array.isRequired,
  acctCategoryData: PropTypes.array.isRequired,
  vendorData: PropTypes.array.isRequired,
  transactionTypesData: PropTypes.array.isRequired,
  initialSelectedSiteIds: PropTypes.array,
  handleCollapsePanelClick: PropTypes.func,
  clearFilters: PropTypes.func,
  handleSubmit: PropTypes.func,
  handleChange: PropTypes.func,
  setSelectedSites: PropTypes.func,
  itemSortOptionsValue: PropTypes.number,
  activeIndexes: PropTypes.array,
  printOptionsValue: PropTypes.number,
  filterValues: PropTypes.object,
  loadingSitesData: PropTypes.bool,
  reactTableRef: PropTypes.object,
};

TransactionDetailsReportComponent.defaultProps = {
  clearFilters: () => {},
  handleSubmit: () => {},
  handleChange: () => {},
  setSelectedSites: () => {},
  itemSortOptionsValue: 0,
  activeIndexes: [0],
  printOptionsValue: 0,
  filterValues: {},
  loadingSitesData: false,
  reactTableRef: null,
};

export default TransactionDetailsReportComponent;



// WEBPACK FOOTER //
// ./src/pages/reports/inventory/transactionhistoryreport/components/TransactionHistoryReport.js